import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";

interface ClientCredentialCreatedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string | null;
  clientSecret: string | null;
  onClose: () => void;
}

export function ClientCredentialCreatedDialog({
  open,
  onOpenChange,
  clientId,
  clientSecret,
  onClose,
}: ClientCredentialCreatedDialogProps) {
  const { t } = useTranslation();
  const [showSecret, setShowSecret] = useState(false);

  const getMaskedSecret = (secret: string | null) => {
    if (!secret) return "";
    const lastFour = secret.slice(-4);
    return "*".repeat(28) + lastFour;
  };

  const getCombinedCredentials = (forCopying: boolean = false) => {
    const secretToUse = forCopying ? clientSecret : showSecret ? clientSecret : getMaskedSecret(clientSecret);
    return `${t("Client ID")}: ${clientId}\n${t("Client Secret")}: ${secretToUse}`;
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("Save Your Client Credentials")}</AlertDialogTitle>
          <AlertDialogDescription className="pt-4">
            {t(
              "Please copy and store these credentials in a secure location. For security reasons, the client secret will not be displayed again after you close this dialog.",
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-center gap-2 rounded-md border p-4">
          <div className="flex-1">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium">{t("Client ID")}:</span>
                <code className="ml-2 break-all text-sm">{clientId}</code>
              </div>
              <div>
                <span className="text-sm font-medium">{t("Client Secret")}:</span>
                <code className="ml-2 break-all text-sm">
                  {getCombinedCredentials()
                    .split("\n")[1]
                    .replace(`${t("Client Secret")}: `, "")}
                </code>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowSecret(!showSecret)}>
              {showSecret ? t("Hide") : t("Show")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(getCombinedCredentials(true));
                toast({
                  title: t("Copied"),
                  description: t("Credentials copied to clipboard"),
                });
              }}
            >
              {t("Copy")}
            </Button>
          </div>
        </div>
        <AlertDialogAction onClick={onClose}>{t("I've saved the credentials")}</AlertDialogAction>
      </AlertDialogContent>
    </AlertDialog>
  );
}
